import { cn } from "@/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { UpgradeSubscriptionButton } from "components/action-buttons/action-buttons";
import { PermissionsKeys } from "components/permissions/permission-keys";
import { PermissionsReasons } from "components/permissions/permission-messages";
import { PermissionsWrapper } from "components/permissions/permissions";
import { usePermissions } from "components/permissions/usePermissions";
import PaymentMethodInfo from "components/settings/shared/billing-details/paymentMethodInfo";
import { useCustomSnackbars } from "components/snackbars/useCustomSnackbars";
import {
  Alert,
  AlertAction,
  AlertDescription,
  AlertTitle,
} from "components/ui/alert";
import { Button } from "components/ui/button";
import { Card, CardContent } from "components/ui/card";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Skeleton } from "components/ui/skeleton";
import { Slider } from "components/ui/slider";
import { Spinner } from "components/ui/spinner";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/ui/tooltip";
import { useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import { updateOverageLimit } from "data/mutations-common";
import { fetchCredits, fetchSubscription } from "data/queries-common";
import _ from "lodash";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useIntercom } from "react-use-intercom";
import * as Routes from "routes";
import { PlanDetails, roundUpOverageDollarLimit } from "utils/calculations";
import { resolveIdentityId } from "utils/identity";

interface UseOverageCreditsCardProps {
  sx?: any;
  className?: string;
  jobCreditCost: number;
  isLoading?: boolean;
  type: JobType;
  setIsEnoughCredits: React.Dispatch<React.SetStateAction<boolean>>;
}

export enum JobType {
  BATCH,
  CLIP,
}

export const UseOverageCreditsCard = (props: UseOverageCreditsCardProps) => {
  const { isPermitted } = usePermissions();
  const [identityState] = useIdentity();
  const identityId = resolveIdentityId(identityState);
  const { show } = useIntercom();
  const history = useHistory();

  let name = "job";
  if (props.type === JobType.BATCH) name = "job";
  if (props.type === JobType.CLIP) name = "clip";

  let action = "start";
  if (props.type === JobType.BATCH) action = "start";
  if (props.type === JobType.CLIP) action = "purchase";

  interface Mark {
    value: number;
    label?: string;
  }

  const [marks, setMarks] = useState<Mark[]>([]);
  const [sliderMin, setSliderMin] = useState(0);
  const [sliderMax, setSliderMax] = useState(10000);
  const [sliderRequired, setSliderRequired] = useState(0);

  const [paymentMethod, setPaymentMethod] = useState<boolean>(false);

  const { enqueueQueryFailed } = useCustomSnackbars();

  const creditsQuery = useQuery({
    queryKey: [QueryKeys.credits, identityId],
    queryFn: () => fetchCredits(),
  });

  useEffect(() => {
    if (creditsQuery.isError) {
      enqueueQueryFailed(creditsQuery.error.toString());
    }
  }, [creditsQuery.isError]);

  useEffect(() => {
    if (
      creditsQuery.isSuccess &&
      creditsQuery.data &&
      newOverageDollarLimit === 0
    ) {
      setNewOverageDollarLimit(creditsQuery.data.overageDollarsLimit);
    }
  }, [creditsQuery.isSuccess]);

  const [newOverageDollarLimit, setNewOverageDollarLimit] = useState<number>(
    creditsQuery.data && creditsQuery.isSuccess
      ? creditsQuery.data.overageDollarsLimit
      : 0,
  );

  const subscriptionQuery = useQuery({
    queryKey: [QueryKeys.subscription, identityId],
    queryFn: () => fetchSubscription(),
  });
  const planDetails: PlanDetails = {
    overageRate: 1,
    planCredits: 1,
  };

  if (
    subscriptionQuery.isSuccess &&
    !!subscriptionQuery.data.activeSubscription
  ) {
    planDetails.overageRate = subscriptionQuery.data.activeSubscription.plan
      .overage_cost_dollars
      ? subscriptionQuery.data.activeSubscription.plan.overage_cost_dollars
      : 1;
    planDetails.planCredits =
      subscriptionQuery.data.activeSubscription.plan.credit_amount;
  }

  const queryClient = useQueryClient();
  const overageMutation = useMutation({
    mutationFn: () => {
      if (
        newOverageDollarLimit === undefined ||
        !creditsQuery.isSuccess ||
        !subscriptionQuery.isSuccess
      ) {
        throw Error(
          "Unable to update overage, incomplete overage information.",
        );
      }

      const clampedNewOverageDollarLimit = _.clamp(
        newOverageDollarLimit,
        sliderMin,
        sliderMax,
      );

      return updateOverageLimit(clampedNewOverageDollarLimit, planDetails);
    },
    onSuccess: (newCreditsInfo) => {
      queryClient.setQueryData([QueryKeys.credits, identityId], newCreditsInfo);
      setNewOverageDollarLimit(newCreditsInfo.overageDollarsLimit);
    },
  });

  const isLoading =
    subscriptionQuery.isPending || creditsQuery.isPending || props.isLoading;
  const isSuccess = subscriptionQuery.isSuccess && creditsQuery.isSuccess;

  const isProfessional: boolean =
    !!subscriptionQuery.data &&
    !!subscriptionQuery.data.activeSubscription &&
    subscriptionQuery.data?.activeSubscription.plan.product_key.includes("pro");

  const hasUnlimitedCredits = isPermitted([PermissionsKeys.creditsUnlimited]);

  var costOverageCredits = 0;
  var costOverageDollars = 0;
  var isOverageRequired = false;
  var isCreditsRemaining = true;
  var remainingCreditsBalance = 0;
  if (isSuccess) {
    costOverageCredits = creditsQuery.data
      ? (creditsQuery.data.subCreditsBalance - props.jobCreditCost) * -1
      : 0;

    costOverageDollars = creditsQuery.data
      ? costOverageCredits * creditsQuery.data.overageRateDollarsPerCredit
      : 0;

    isOverageRequired = costOverageCredits > 0;

    remainingCreditsBalance = creditsQuery.data
      ? creditsQuery.data.totalCreditsBalance - props.jobCreditCost
      : 0;

    isCreditsRemaining = remainingCreditsBalance >= 0 || hasUnlimitedCredits;
  }

  if (props.setIsEnoughCredits) props.setIsEnoughCredits(isCreditsRemaining);

  useEffect(() => {
    if (
      creditsQuery.data &&
      creditsQuery.isSuccess &&
      isOverageRequired &&
      !hasUnlimitedCredits
    ) {
      var requiredAdditionalOverageLimit = isCreditsRemaining
        ? costOverageDollars
        : -remainingCreditsBalance *
          creditsQuery.data.overageRateDollarsPerCredit;
      requiredAdditionalOverageLimit = roundUpOverageDollarLimit(
        requiredAdditionalOverageLimit,
        creditsQuery.data.overageRateDollarsPerCredit,
      );

      const startSliderMark = creditsQuery.data.overageDollarsLimit;
      const incrementSliderMark =
        creditsQuery.data.overageDollarsPerCreditsBlock;
      const requiredSliderMark =
        creditsQuery.data.overageDollarsLimit + requiredAdditionalOverageLimit;
      const endSliderMark = Math.min(
        creditsQuery.data.overageDollarsLimit +
          requiredAdditionalOverageLimit * 2,
        creditsQuery.data.overageDollarsMax,
      );

      const sliderMarks = [];
      for (
        var i = startSliderMark;
        i <= endSliderMark;
        i += incrementSliderMark
      ) {
        const isLastIteration = i + incrementSliderMark > endSliderMark;
        const s = isLastIteration ? endSliderMark : i;
        const isShowLabel =
          i === startSliderMark || i === requiredSliderMark || isLastIteration;
        const sliderMark: Mark = {
          value: i,
          ...(isShowLabel && { label: `$${i.toLocaleString()}` }),
        };

        sliderMarks.push(sliderMark);
      }

      setMarks(sliderMarks);
      if (sliderMarks.length > 0) {
        setSliderMin(sliderMarks[0].value);
        setSliderMax(sliderMarks[sliderMarks.length - 1].value);
        setSliderRequired(requiredSliderMark);
      }
    } else {
      setMarks([]);
    }
  }, [creditsQuery.data, isOverageRequired]);

  if (isLoading) {
    return <Skeleton className={cn("h-full w-full", props.className)} />;
  }

  const borderColour =
    isCreditsRemaining && isOverageRequired && !hasUnlimitedCredits
      ? "border-info"
      : !isCreditsRemaining && isOverageRequired && !hasUnlimitedCredits
        ? newOverageDollarLimit < sliderRequired
          ? "border-destructive"
          : "border-success"
        : "";

  const step = marks.length > 1 ? marks[1].value - marks[0].value : 1;

  return (
    <Card className={cn(borderColour, props.className)}>
      <CardContent>
        {/* We HAVE enough credits and overage is NOT required */}
        {(isCreditsRemaining || hasUnlimitedCredits) && !isOverageRequired && (
          <div>
            <h3 className="text-lg font-medium">
              {_.startCase(action)} {_.startCase(name)}
            </h3>
            <p className="text-sm text-muted-foreground">
              Review your {name}, then {action} it.
              {props.type === JobType.BATCH &&
                " Unused credits are refunded; failed batches are not charged."}
            </p>
          </div>
        )}

        {/* ENTERPRISE/MYOB and overage IS required */}
        {hasUnlimitedCredits && isOverageRequired && creditsQuery.isSuccess && (
          <div>
            <h3 className="text-lg font-medium">
              {_.startCase(action)} {_.startCase(name)}
            </h3>
            <div className="flex flex-col gap-2 mt-2">
              <p className="text-sm">
                Overage required: {costOverageCredits.toLocaleString()} credits.
              </p>
              <p className="text-sm text-muted-foreground">
                You’ll be billed for actual overage usage.
              </p>
            </div>
          </div>
        )}

        {/* We do NOT have enough credits and overage IS required */}
        {!hasUnlimitedCredits &&
          isOverageRequired &&
          creditsQuery.isSuccess && (
            <PermissionsWrapper
              keys={[PermissionsKeys.overageUpdate]}
              messages={{
                [PermissionsReasons.UPGRADE_REQUIRED]:
                  getPermissionMessage(name),
                [PermissionsReasons.FREE_TRIAL]: getPermissionMessage(name),
              }}
            >
              <div className="flex flex-col gap-2">
                {isCreditsRemaining && creditsQuery.data && (
                  <div>
                    <h3 className="text-lg font-medium text-info">
                      Overage Notification
                    </h3>
                    <div className="flex flex-col gap-2 mt-2">
                      <p className="text-sm">
                        Your limit covers this clip. Recommended increase: $
                        {costOverageDollars.toLocaleString()}.
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Billed only for actual overage usage.
                      </p>
                    </div>
                  </div>
                )}

                {!isCreditsRemaining && creditsQuery.data && (
                  <div>
                    <h3
                      className={cn(
                        "text-lg font-medium",
                        newOverageDollarLimit < sliderRequired
                          ? "text-destructive"
                          : "text-success",
                      )}
                    >
                      Insufficient Balance
                    </h3>

                    <div className="flex flex-col gap-2 mt-2">
                      <p className="text-sm">
                        Increase your limit by at least $
                        {(
                          (isCreditsRemaining ? 1 : -1) *
                          remainingCreditsBalance *
                          creditsQuery.data.overageRateDollarsPerCredit
                        ).toLocaleString()}
                        .
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Billed only for actual overage usage.
                      </p>
                    </div>
                  </div>
                )}

                <PaymentMethodInfo
                  setPaymentMethod={setPaymentMethod}
                  hideTitle={true}
                />

                <div className="flex flex-col gap-4 pt-3">
                  <Slider
                    value={[newOverageDollarLimit]}
                    onValueChange={(values) =>
                      setNewOverageDollarLimit(values[0])
                    }
                    min={sliderMin}
                    max={sliderMax}
                    step={step}
                    rangeClassName={cn(
                      newOverageDollarLimit < sliderRequired
                        ? "bg-destructive"
                        : "bg-success",
                    )}
                  />
                  {marks.length > 0 && (
                    <div className="flex text-xs text-muted-foreground -mt-2">
                      {marks
                        .filter((m) => m.label)
                        .map((m, index, labelledMarks) => (
                          <span
                            className={cn(
                              "flex-1",
                              index === 0
                                ? "text-left"
                                : index === labelledMarks.length - 1
                                  ? "text-right"
                                  : "text-center ml-3",
                            )}
                            key={m.value}
                          >
                            {m.label}
                          </span>
                        ))}
                    </div>
                  )}
                  {creditsQuery.isSuccess && creditsQuery.data && (
                    <div className="flex flex-col w-full gap-3 items-end">
                      <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2">
                        <div className="flex min-w-0 flex-col gap-1">
                          <Label htmlFor="budget">Budget ($)</Label>
                          <Input
                            id="budget"
                            value={newOverageDollarLimit}
                            onChange={(event) => {
                              const newValue = +event.target.value;
                              setNewOverageDollarLimit(newValue);
                            }}
                          />
                          <p className="text-xs text-muted-foreground">
                            Rounded to $
                            {creditsQuery.data.overageDollarsPerCreditsBlock}{" "}
                            increments.
                          </p>
                        </div>

                        <div className="flex min-w-0 flex-col gap-1">
                          <Label htmlFor="credit-budget">Credit budget</Label>
                          <Input
                            id="credit-budget"
                            value={
                              newOverageDollarLimit /
                              creditsQuery.data.overageRateDollarsPerCredit
                            }
                            onChange={(event) => {
                              const newValue =
                                +event.target.value *
                                creditsQuery.data!!.overageRateDollarsPerCredit;
                              setNewOverageDollarLimit(newValue);
                            }}
                          />
                          <p className="text-xs text-muted-foreground">
                            Rounded to 1,000-credit increments.
                          </p>
                        </div>
                      </div>

                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span>
                            <Button
                              onClick={() => overageMutation.mutate()}
                              disabled={
                                !paymentMethod || overageMutation.isPending
                              }
                            >
                              {overageMutation.isPending && <Spinner />}
                              Save
                            </Button>
                          </span>
                        </TooltipTrigger>
                        {!paymentMethod && (
                          <TooltipContent>
                            Requires payment method
                          </TooltipContent>
                        )}
                      </Tooltip>
                    </div>
                  )}

                  {!isProfessional && (
                    <div className="rounded-lg border border-info/50 bg-info/10 p-2 text-info-foreground">
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-xs font-semibold">
                          Prefer not to use Overage?
                        </p>
                        <Button
                          className="shrink-0"
                          size="sm"
                          onClick={() =>
                            history.push(Routes.orgManageSubscription)
                          }
                        >
                          Manage Subscription
                        </Button>
                      </div>
                      <p className="mt-1 text-xs text-muted-foreground">
                        Manage your subscription instead.
                      </p>
                    </div>
                  )}

                  {isProfessional && (
                    <div className="rounded-lg border border-info/50 bg-info/10 p-3 text-info-foreground">
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-xs font-semibold">
                          Prefer not to use Overage?
                        </p>
                        <Button
                          className="shrink-0"
                          size="sm"
                          onClick={() => show()}
                        >
                          Contact Us
                        </Button>
                      </div>
                      <p className="mt-1 text-xs text-muted-foreground">
                        Talk to us about an Enterprise plan.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </PermissionsWrapper>
          )}
      </CardContent>
    </Card>
  );
};

const getPermissionMessage = (name: string) =>
  name === "clip" ? <UpgradeAlert /> : <CantUseOverageCard />;

const UpgradeAlert = () => (
  <Alert variant="info">
    <AlertTitle>Not enough credits</AlertTitle>
    <AlertDescription>Upgrade to a paid plan.</AlertDescription>
    <AlertAction>
      <UpgradeSubscriptionButton />
    </AlertAction>
  </Alert>
);

const CantUseOverageCard = () => (
  <Card className="h-full flex flex-col justify-between">
    <CardContent>
      <h3 className="text-lg font-medium text-warning">Insufficient Balance</h3>
      <Alert variant="info" className="mt-2">
        <AlertTitle>Not enough credits</AlertTitle>
        <AlertDescription>Upgrade to a paid plan.</AlertDescription>
        <AlertAction>
          <UpgradeSubscriptionButton />
        </AlertAction>
      </Alert>
    </CardContent>
  </Card>
);
